import { useState, useEffect, useRef } from "react";
import axios from "axios";
import Pagination from "../components/newpagination";
import Layout from "../containers/layout";
import Cookies from "js-cookie";
import * as XLSX from "xlsx";
import {
  Page,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  TextField,
  Spinner,
  Box,
  Grid,
  Toast,
} from "@shopify/polaris";
import CouponCardTable from "../components/couponCardTable";

const CouponCard = () => {
  const [products, setProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState({ from: "", till: "" });
  const [notification, setNotification] = useState(null);
  const [isFiltered, setIsFiltered] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [stats, setStats] = useState({ total: 0, used: 0, unused: 0 });
  const perPage = 50;
  const token = Cookies.get("token");
  const fileInputRef = useRef(null);
  const showNotification = (tone, title) => {
    setNotification({ tone, title });
    setTimeout(() => setNotification(null), 5000);
  };
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchData = async (page = 1) => {
    try {
      setLoading(true);
      let url = "https://api.iplanet.one/coupon/getall/coupons";
      let params = {
        page,
        limit: perPage,
      };

      const hasDateFilter = date.from && date.till;
      const hasSearch = debouncedSearch.trim().length > 0;

      if (hasDateFilter || hasSearch) {
        url = "https://api.iplanet.one/coupon/getall/coupons/filter";
        params = {
          ...params,
          ...(hasSearch && { coupon: debouncedSearch }),
          ...(hasDateFilter && {
            startDate: date.from,
            endDate: date.till,
          }),
        };
      }

      const res = await axios.get(url, {
        params,
        headers: {
          Authorization: token,
        },
      });

      const data = Array.isArray(res?.data?.codes) ? res.data.codes : [];
      setProducts(data);
      setTotalPages(res?.data?.pagination?.totalPages || 1);
      setStats({
        total: res?.data?.pagination?.total || 0,
        used: res?.data?.used || 0,
        unused: res?.data?.unused || 0,
      });
    } catch (err) {
      console.error(err);
      showNotification("critical", "Failed to load Data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(currentPage);
  }, [currentPage, isFiltered, debouncedSearch]);

  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [debouncedSearch, isFiltered]);

  const FilterHandler = () => {
    if (!date.from || !date.till) {
      showNotification("warning", "Please select both dates");
      return;
    }
    setCurrentPage(1);
    setIsFiltered(true);

    fetchData(1);
  };

  const clearFilter = () => {
    setDate({ from: "", till: "" });
    setIsFiltered(false);
    setCurrentPage(1);

    fetchData(1);
  };

  const handleDownload = () => {
    try {
      const formattedData = products.map((item) => {
        const { _id, updatedAt, __v, ...rest } = item;
        const formattedItem = {};
        Object.keys(rest).forEach((key) => {
          const newKey = key
            .replace(/([A-Z])/g, " $1")
            .replace(/^./, (str) => str.toUpperCase());
          formattedItem[newKey] = rest[key];
        });
        return formattedItem;
      });

      const worksheet = XLSX.utils.json_to_sheet(formattedData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Coupon");
      XLSX.writeFile(workbook, "Coupon_Code.xlsx");

      showNotification("success", "Exported successfully");
    } catch (error) {
      showNotification("critical", "Export failed");
    }
  };
  const handleImport = async (e) => {
    const file = e.target.files[0];

    if (!file) return;

    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("file", file);

      await axios.post("https://api.iplanet.one/coupon/upload", formData, {
        headers: {
          Authorization: token,
          "Content-Type": "multipart/form-data",
        },
      });

      showNotification("success", "Coupons imported successfully");

      fetchData(currentPage);

      e.target.value = "";
    } catch (err) {
      console.error(err);
      showNotification("critical", "Import failed");
    } finally {
      setLoading(false);
    }
  };
  return (
    <Layout>
      <Page fullWidth>
        <BlockStack gap="500">
          {notification && (
            <Toast
              content={notification.title}
              tone={notification.tone}
              onDismiss={() => setNotification(null)}
            />
          )}
          <InlineStack gap="400" wrap={false}>
            <div style={{ flex: 1 }}>
              <Card>
                <BlockStack gap="200">
                  <Text variant="bodyMd" tone="subdued">
                    Total Coupons
                  </Text>
                  <Text variant="heading2xl">{stats.total}</Text>
                </BlockStack>
              </Card>
            </div>
            <div style={{ flex: 1 }}>
              <Card>
                <BlockStack gap="200">
                  <Text variant="bodyMd" tone="subdued">
                    Used
                  </Text>
                  <Text variant="heading2xl">{stats.used}</Text>
                </BlockStack>
              </Card>
            </div>
            <div style={{ flex: 1 }}>
              <Card>
                <BlockStack gap="200">
                  <Text variant="bodyMd" tone="subdued">
                    Unused
                  </Text>
                  <Text variant="heading2xl">{stats.unused}</Text>
                </BlockStack>
              </Card>
            </div>
          </InlineStack>

          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text variant="headingLg">Coupon Card Table</Text>
                <InlineStack gap="200">
                  <Button variant="primary" onClick={handleDownload}>
                    Export
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => fileInputRef.current.click()}
                  >
                    Import
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    style={{ display: "none" }}
                    onChange={handleImport}
                  />
                  {isFiltered && (
                    <Button onClick={clearFilter}>Clear Filter</Button>
                  )}
                </InlineStack>
              </InlineStack>

              <Card background="bg-surface-secondary" rounded="300">
                <Box padding="400">
                  <Grid
                    columns={{ xs: 1, sm: 6, md: 12, lg: 12 }}
                    gap={{ xs: "400", md: "500" }}
                  >
                    <Grid.Cell columnSpan={{ xs: 1, md: 5, lg: 5 }}>
                      <TextField
                        label="From Date"
                        type="date"
                        value={date.from}
                        onChange={(val) => setDate({ ...date, from: val })}
                      />
                    </Grid.Cell>
                    <Grid.Cell columnSpan={{ xs: 1, md: 5, lg: 5 }}>
                      <TextField
                        label="Till Date"
                        type="date"
                        value={date.till}
                        onChange={(val) => setDate({ ...date, till: val })}
                      />
                    </Grid.Cell>
                    <Grid.Cell columnSpan={{ xs: 1, md: 2 }}>
                      <div
                        style={{
                          height: "100%",
                          display: "flex",
                          alignItems: "flex-center",
                          paddingTop: "var(--p-space-600)",
                        }}
                      >
                        <Button
                          variant="primary"
                          onClick={FilterHandler}
                          loading={loading}
                          disabled={
                            loading ||
                            !date.from ||
                            !date.till ||
                            date.from > date.till
                          }
                          fullWidth
                        >
                          Filter
                        </Button>
                      </div>
                    </Grid.Cell>
                  </Grid>
                </Box>
              </Card>
            </BlockStack>
          </Card>
          <Card>
            {loading ? (
              <div style={{ padding: "120px 0", textAlign: "center" }}>
                <Spinner size="large" />
              </div>
            ) : (
              <BlockStack gap="400">
                <CouponCardTable
                  products={products}
                  indexing={currentPage}
                  perPage={perPage}
                  search={search}
                  setSearch={setSearch}
                />

                <div style={{ padding: "40px 0", textAlign: "center" }}>
                  <Pagination
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    totalPages={totalPages}
                  />
                </div>
              </BlockStack>
            )}
          </Card>
        </BlockStack>
      </Page>
    </Layout>
  );
};

export default CouponCard;
